// Einstellungen › Sicherheit (docs/anmeldung/SPEZIFIKATION.md §6.7, §6.12, §8.2, §10.3): Anmelderichtlinie des
// Betriebs (Zwei-Faktor-Pflicht für alle oder ausgewählte Rollen, Übergangsfrist, vertraute Geräte, Übergabe-Links),
// Übersicht „Zwei-Faktor: 9 von 14 Personen“ und das Anmeldeprotokoll der Mitglieder – dazu der Sicherheitsstand der
// angemeldeten Person für ihr Profil und der Schalter „Support-Zugang des Betreibers erlauben“ (§18). Ändern ist
// sicherheitsrelevant (frische Bestätigung) und nie über den Assistenten.
import { PERMISSION_AUTHENTICATED, defineContract } from '@shared/contract'
import { zId } from '@shared/ids'
import { SECURITY_POLICY_DAYS } from '@shared/auth'
import { z } from 'zod'

const zDays = z.int({ error: 'Bitte geben Sie eine ganze Zahl von Tagen an.' }).min(SECURITY_POLICY_DAYS.min, {
  error: `Bitte geben Sie mindestens ${SECURITY_POLICY_DAYS.min} Tage an.`,
}).max(SECURITY_POLICY_DAYS.max, { error: `Bitte geben Sie höchstens ${SECURITY_POLICY_DAYS.max} Tage an.` })

/** Wie die Pflicht gilt: niemand · ausgewählte Rollen · alle Personen des Betriebs */
export const MFA_POLICY_MODES = ['none', 'roles', 'all'] as const
export type MfaPolicyMode = (typeof MFA_POLICY_MODES)[number]
export const MFA_POLICY_MODE_LABELS: Record<MfaPolicyMode, string> = {
  none: 'Niemand',
  roles: 'Ausgewählte Rollen',
  all: 'Alle Personen',
}

export const zSecurityPolicy = z.object({
  mode: z.enum(MFA_POLICY_MODES),
  mfaRequiredAll: z.boolean(),
  /** Beginn der Pflicht für alle (ISO) */
  mfaEnforceFrom: z.string().nullable(),
  mfaGraceDays: z.int(),
  trustedDeviceDays: z.int(),
  handoverLinks: z.boolean(),
  updatedAt: z.string().nullable(),
  /** null = noch nie gespeichert (Standardwerte) */
  version: z.int().nullable(),
})

export const zSecurityRole = z.object({
  id: zId,
  key: z.string(),
  name: z.string(),
  mfaRequired: z.boolean(),
  /** Vorgabe bis zur Entscheidung (§15.1 F4): Pflicht empfohlen – Geschäftsführung, Buchhaltung, Rollen mit Verwaltungsrechten */
  recommended: z.boolean(),
  /** Vorgabe: ausdrücklich ohne Pflicht (Kasse, Lager, Fahrer, Schnittstellen) */
  exempt: z.boolean(),
  /** Personen mit dieser Rolle */
  members: z.int().nonnegative(),
})

export const zSecurityOverview = z.object({
  /** Personen des Betriebs ohne technische Zugänge */
  people: z.int().nonnegative(),
  mfaOn: z.int().nonnegative(),
  /** Pflicht, noch nicht eingerichtet, Frist läuft */
  withDeadline: z.int().nonnegative(),
  /** Pflicht, nicht eingerichtet, Frist abgelaufen (Einrichtung bei der nächsten Anmeldung erzwungen) */
  overdue: z.int().nonnegative(),
  /** ohne Pflicht und ohne Zwei-Faktor */
  notRequired: z.int().nonnegative(),
  /** „Zwei-Faktor: 9 von 14 Personen, 3 mit Frist, 2 ohne Pflicht“ */
  line: z.string(),
})

/** Support-Zugang des Betreibers (docs/anmeldung/SPEZIFIKATION.md §18) in Einstellungen › Sicherheit */
export const zSupportAccess = z.object({
  /** Schalter „Support-Zugang des Betreibers erlauben“ (Vorgabe an) */
  allowed: z.boolean(),
  /** nur die eigene Geschäftsführung schaltet (Rolle mit allen Rechten, nicht der Support-Zugang selbst) */
  canChange: z.boolean(),
  /** Support-Zugänge, die es in diesem Betrieb gibt bzw. gab */
  holders: z.array(
    z.object({
      membershipId: zId,
      displayName: z.string(),
      /** 'active' = kann arbeiten · 'disabled' = gesperrt (Schalter aus bzw. nicht mehr Betreiber) */
      status: z.enum(['active', 'disabled']),
      /** zuletzt geöffnet (ISO) */
      lastOpenedAt: z.string().nullable(),
    }),
  ),
})

export const securityGet = defineContract({
  name: 'platform.security.get',
  title: 'Anmelderichtlinie anzeigen',
  description: 'Zwei-Faktor-Pflicht, Übergangsfrist, vertraute Geräte und Übergabe-Links des Betriebs – mit der Übersicht, wie viele Personen die Zwei-Faktor-Anmeldung nutzen.',
  llm: {
    description:
      'Liefert die Anmelderichtlinie des Betriebs (wer die Zwei-Faktor-Anmeldung braucht, Frist, vertraute Geräte) und wie ' +
      'viele Personen sie eingerichtet haben. Verwenden für „Müssen bei uns alle einen Code eingeben?“ oder „Wer hat noch keine Zwei-Faktor-Anmeldung?“.',
    keywords: ['Zwei-Faktor', '2FA', 'Sicherheit', 'Anmeldung', 'Pflicht', 'Code', 'Richtlinie'],
  },
  input: z.strictObject({}),
  output: z.object({
    policy: zSecurityPolicy,
    roles: z.array(zSecurityRole),
    overview: zSecurityOverview,
    /** Demo-Betrieb: keine Zwei-Faktor-Anmeldung, Pflicht nicht einstellbar */
    demo: z.boolean(),
    support: zSupportAccess,
  }),
  permission: 'platform.user.read',
  risk: 'read',
})

export const securityUpdate = defineContract({
  name: 'platform.security.update',
  title: 'Anmelderichtlinie ändern',
  description:
    'Legt fest, wer die Zwei-Faktor-Anmeldung braucht (niemand, ausgewählte Rollen, alle), die Übergangsfrist, wie lange Geräte vertraut bleiben und ob Links zum Weitergeben erlaubt sind. Die Pflicht greift bei der nächsten Anmeldung.',
  llm: { description: 'Ändert die Anmelderichtlinie. Nur für die Oberfläche der Verwaltung.', keywords: [] },
  input: z.strictObject({
    mode: z.enum(MFA_POLICY_MODES).optional(),
    /** bei `roles`: Rollen mit Pflicht (alle übrigen ohne) */
    roleIds: z.array(zId).max(200).optional(),
    mfaGraceDays: zDays.optional(),
    trustedDeviceDays: zDays.optional(),
    handoverLinks: z.boolean().optional(),
    /** Version beim Laden (gleichzeitige Änderung) */
    version: z.int().nullish(),
  }),
  output: z.object({ policy: zSecurityPolicy, roles: z.array(zSecurityRole), changed: z.array(z.string()) }),
  permission: 'platform.security.manage',
  risk: 'write',
  effects: { securitySensitive: true },
  assistant: 'never',
})

export const securitySupportAccess = defineContract({
  name: 'platform.security.setSupportAccess',
  title: 'Support-Zugang des Betreibers erlauben oder sperren',
  description:
    'Schaltet den Support-Zugang des Betreibers für diesen Betrieb ein oder aus. Aus: Der Betreiber kann den Betrieb nicht mehr wählen, laufende Support-Sitzungen enden sofort. Nur für die Geschäftsführung des Betriebs.',
  llm: { description: 'Schaltet den Support-Zugang des Betreibers. Nur für die Oberfläche der Geschäftsführung.', keywords: [] },
  input: z.strictObject({ allowed: z.boolean() }),
  output: z.object({
    allowed: z.boolean(),
    /** dabei gesperrte Support-Zugänge */
    closed: z.int().nonnegative(),
    /** dabei beendete Sitzungen */
    endedSessions: z.int().nonnegative(),
  }),
  permission: 'platform.security.manage',
  risk: 'write',
  effects: { securitySensitive: true },
  assistant: 'never',
})

export const zSecurityLogEntry = z.object({
  id: zId,
  at: z.string(),
  kind: z.string(),
  /** in Worten: „Angemeldet“, „Falscher Code“ … */
  label: z.string(),
  membershipId: zId,
  displayName: z.string(),
  /** gekürzt auf /24 bzw. /48 */
  ip: z.string().nullable(),
  deviceLabel: z.string().nullable(),
  /** von der Verwaltung ausgelöst: Name der handelnden Person */
  byName: z.string().nullable(),
  /** kurzer Zusatz in Worten („über einen Wiederherstellungscode“, „3 Sitzungen“) */
  note: z.string().nullable(),
})

export const securityEvents = defineContract({
  name: 'platform.security.events',
  title: 'Anmeldeprotokoll der Mitglieder',
  description:
    'Anmeldungen, Fehlversuche, Codes, Kennwort- und Zwei-Faktor-Änderungen der Personen dieses Betriebs – mit gekürzter IP-Adresse.',
  llm: {
    description:
      'Liefert das Anmeldeprotokoll der Mitglieder (Anmeldungen, Fehlversuche, Codes, Kennwortwechsel, Zwei-Faktor) mit ' +
      'gekürzter IP-Adresse; filterbar nach Person, Art und Zeitraum. Verwenden für „Wann hat sich Lea zuletzt angemeldet?“ ' +
      'oder „Gab es Fehlversuche bei Emre?“.',
    keywords: ['Anmeldeprotokoll', 'Fehlversuche', 'angemeldet', 'Sicherheit', 'Zwei-Faktor', 'Kennwort geändert'],
  },
  input: z.strictObject({
    membershipId: zId.nullish(),
    /** Art bzw. Gruppe: 'login', 'mfa', 'password', 'session', 'device' oder eine genaue Art ('login.failed') */
    kind: z.string().trim().regex(/^[a-z_]+(?:\.[a-z_]+)?$/).nullish(),
    /** erster Tag (Europe/Berlin), einschließlich */
    from: z.iso.date().nullish(),
    to: z.iso.date().nullish(),
    limit: z.int().min(1).max(500).default(100),
  }),
  output: z.object({
    events: z.array(zSecurityLogEntry),
    /** es gibt mehr Einträge als angezeigt */
    truncated: z.boolean(),
    people: z.array(z.object({ membershipId: zId, displayName: z.string() })),
  }),
  permission: 'platform.user.read',
  risk: 'read',
})

// ---- Profil: Sicherheitsstand der angemeldeten Person (§10.2) ------------------------------------------------------

export const profileSecurity = defineContract({
  name: 'platform.profile.security',
  title: 'Eigener Sicherheitsstand',
  description: 'Wann Ihr Kennwort zuletzt geändert wurde, ob die Zwei-Faktor-Anmeldung an ist und ob Ihr Betrieb sie verlangt.',
  llm: {
    description:
      'Sicherheitsstand der angemeldeten Person: Kennwort zuletzt geändert, Zwei-Faktor an oder aus, Pflicht und Frist, ' +
      'verbliebene Wiederherstellungscodes. Verwenden für „Muss ich die Zwei-Faktor-Anmeldung einrichten?“.',
    keywords: ['Kennwort', 'Zwei-Faktor', 'Sicherheit', 'mein Konto'],
  },
  input: z.strictObject({}),
  output: z.object({
    passwordChangedAt: z.string().nullable(),
    mfa: z.object({
      enabled: z.boolean(),
      confirmedAt: z.string().nullable(),
      recoveryCodesLeft: z.int().nonnegative().nullable(),
      /** dieser Betrieb verlangt sie für die Person */
      required: z.boolean(),
      dueAt: z.string().nullable(),
    }),
    /** so viele Tage darf „Diesem Gerät vertrauen“ in diesem Betrieb gelten (0 = nie) */
    trustedDeviceDays: z.int(),
    /** Demo-Betrieb: Kennwort und Zwei-Faktor lassen sich nicht ändern */
    demo: z.boolean(),
    technical: z.boolean(),
  }),
  permission: PERMISSION_AUTHENTICATED,
  risk: 'read',
})
